import { useState, useMemo } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { FolderSelectTree } from '@/components/FolderSelectTree'
import { Loader2, AlertTriangle } from 'lucide-react'
import type { LibraryFolder } from '@/lib/schemas'

interface DeleteFolderDialogProps {
  folder: LibraryFolder
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteFolderDialog({
  folder,
  open,
  onOpenChange,
}: DeleteFolderDialogProps) {
  const { deleteFolder, documents, folders, getChildFolders } = useLibraryStore()
  const [isDeleting, setIsDeleting] = useState(false)
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null)

  // Collect all folder IDs being deleted (this folder + all descendants)
  const deletedFolderIds = useMemo(() => {
    const ids = new Set<string>([folder.id])
    const collectChildren = (parentId: string) => {
      const children = getChildFolders(parentId)
      for (const child of children) {
        ids.add(child.id)
        collectChildren(child.id)
      }
    }
    collectChildren(folder.id)
    return ids
  }, [folder.id, getChildFolders])

  // Count affected items
  const { childFolderCount, documentCount } = useMemo(() => {
    const childCount = deletedFolderIds.size - 1 // Exclude the folder itself
    const docCount = documents.filter(
      (doc) => doc.folderId && deletedFolderIds.has(doc.folderId)
    ).length
    return { childFolderCount: childCount, documentCount: docCount }
  }, [deletedFolderIds, documents])

  // Check if there are other folders available to move docs to
  const hasOtherFolders = useMemo(() => {
    return folders.some((f) => !deletedFolderIds.has(f.id))
  }, [folders, deletedFolderIds])

  // Determine if we can proceed with deletion
  const isLastFolderWithDocs = documentCount > 0 && !hasOtherFolders
  const needsTargetFolder = documentCount > 0 && hasOtherFolders
  const canDelete = isDeleting
    ? false
    : needsTargetFolder
      ? targetFolderId !== null
      : !isLastFolderWithDocs

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await deleteFolder(folder.id, targetFolderId ?? undefined)
      onOpenChange(false)
      setTargetFolderId(null)
    } catch {
      // Error handled in store
    } finally {
      setIsDeleting(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) setTargetFolderId(null)
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Delete Folder</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              <p>
                Are you sure you want to delete <strong>&ldquo;{folder.name}&rdquo;</strong>?
                This action cannot be undone.
              </p>
              {childFolderCount > 0 && (
                <p className="text-sm">
                  {childFolderCount} subfolder{childFolderCount !== 1 ? 's' : ''} will also be
                  deleted.
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Last folder warning — can't delete if docs exist and no other folders */}
        {isLastFolderWithDocs && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              This is the only folder in your library and it contains {documentCount} document
              {documentCount !== 1 ? 's' : ''}. Create another folder first to move documents
              into before deleting this one.
            </p>
          </div>
        )}

        {/* Target folder picker — shown when documents need to be relocated */}
        {needsTargetFolder && (
          <div className="space-y-2">
            <Label>
              Move {documentCount} document{documentCount !== 1 ? 's' : ''} to
            </Label>
            <FolderSelectTree
              selectedFolderId={targetFolderId}
              onSelect={setTargetFolderId}
              excludeIds={deletedFolderIds}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={!canDelete}
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
